import { Box, IconButton, Paragraph, Text } from '@zillow/constellation-10';
import { IconStar0Percent, IconStar100Percent } from '@zillow/constellation-icons';
import { VisuallyHidden } from '@zillow/constellation';
import { format, isThisYear, isToday, isYesterday } from 'date-fns';
import type React from 'react';
import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ConversationListConversation } from '../..';
import StringConstants from '../../../../constants/StringConstants';
import {
  emitStarConversationEvent,
  STAR_CONVERSATION_EVENT_TYPE_ID,
} from '../../../../constants/clickstreamEvents/starConversations';
import { IconArchived, IconSpam } from '../../../Icons';
import { AppContext } from '../../../../contexts/AppContextProvider';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import conversationUtils from '../../../../utils/conversationUtils';
import ConditionalWrapper from '../../../ConditionalWrapper';
import StatusLabel from '../../../StatusLabel';
import {
  StyledIconError,
  StyledListboxOption,
  StyledSpanItemIconContainer,
  StyledUnreadBadgeIndicator,
} from './StyledComponents';
import SwipeableConversationItem from './SwipeableConversationItem';
import useConversationActions from '../../../../hooks/useConversationActions';
import useDevices from '../../../../hooks/useDevices';
import { FULLSTORY_EXCLUDE } from '../../../../constants/fullstory';
import { truncatedTextStyles } from '../../../../styles/text';
import { IconButtonTooltip, MESSAGE_STATUS } from '@zg-rentals/satellite-components';
import { useAuth } from '@zg-rentals/auth-comet';
import { CONVERSATION_ITEM_SELECT_EVENT_TYPE_ID } from '../../../../constants/clickstreamEvents/conversationItem';

const DATE_FNS_FORMAT = 'MMM do';
const DATE_FNS_FORMAT_WITH_YEAR = 'MMM do, yyyy';

const getMessageText = (isMessageOwner: boolean, messagePreview: string) => {
  return isMessageOwner ? `You: ${messagePreview}` : messagePreview;
};

const getMessageDate = (messageDateMs = ''): string => {
  if (isYesterday(+messageDateMs)) {
    return 'Yesterday';
  } else if (isToday(+messageDateMs)) {
    return 'Today';
  }

  const formatToken = isThisYear(+messageDateMs) ? DATE_FNS_FORMAT : DATE_FNS_FORMAT_WITH_YEAR;
  return format(new Date(+messageDateMs), formatToken);
};

type Props = {
  hideListingLabel: boolean;
  listboxOptionProps: ListboxOptionPropsForConversationItem;
  shouldScrollToConversationItem: boolean;
};

export type ListboxOptionPropsForConversationItem = {
  active: boolean;
  ['aria-selected']: boolean;
  conversation: ConversationListConversation;
  id: string;
  label: string;
  value: string;
};

const ConversationItem = ({
  hideListingLabel = false,
  listboxOptionProps,
  shouldScrollToConversationItem,
}: Props): JSX.Element => {
  const [isConversationBlurred, setIsConversationBlurred] = useState(false);
  const { user } = useAuth();
  const { isImpersonator } = useContext(AppContext);
  const { isMobileView } = useDevices();
  const { markConversationAsFavoriteOrUnfavorite } = useConversationActions();
  const { conversationId: currentConversationId } = useContext(InboxPageContext);
  const isFirstRender = useRef(true);
  const { conversation: conversationObj, active } = listboxOptionProps;
  const {
    address,
    conversation,
    conversationId,
    activeParticipantsInConversation,
    referenceName,
    hasUnreadMessage,
    inquiry,
    isConversationBlockedByZillow,
    status,
    statusLabel,
  } = conversationObj;

  const { isArchived, isFavorite, isSpam } = status;

  const filteredConversations = conversation.filter(({ messagePreview }) => messagePreview);
  const { messagePreview, messageDateMs, isMessageOwner, uiTreatmentType } =
    filteredConversations[filteredConversations.length - 1] || {}; // Most recent message sent or received

  const isPhoneLead = inquiry?.isPhoneLead || false;
  const participantDisplay = isPhoneLead
    ? conversationUtils.getPhoneLeadParticipantDisplay({
        name: inquiry?.name || '',
        phoneNumber: inquiry?.phone || '',
      })
    : conversationUtils.getParticipantsNameWithCount({
        activeParticipantsInConversation,
        referenceName: referenceName!,
      });
  const messageDateText = useMemo(() => getMessageDate(messageDateMs), [messageDateMs]);
  const addressText = address?.split(',')[0] || '';
  const messageText = isPhoneLead ? '(Phone lead)' : getMessageText(isMessageOwner || false, messagePreview || '');
  const shouldShowAddress = !hideListingLabel && !!addressText;
  const isCurrentConversation = conversationId === currentConversationId;
  const isConversationActive = active;
  const lastMessageSendStatus = uiTreatmentType?.includes('-') ? uiTreatmentType.split('-')[1] : MESSAGE_STATUS.SENT;
  const lastMessageFailed = lastMessageSendStatus === MESSAGE_STATUS.FAILED;
  const isStarConversationIconDisabled =
    isImpersonator || isSpam || isArchived || (!isFavorite && isConversationBlockedByZillow);

  // Marks a conversation temporarily as read if hasUnreadMessage
  // is set on the BE and it's selected/is current conversation
  // Checking isFirstRender prevents the current conversation from being marked as read on load
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
  }, [isCurrentConversation]);

  const activeConversationRef = (node: HTMLDivElement | null) => {
    if (node) {
      node.scrollIntoView({
        block: 'center',
        inline: 'center',
      });
    }
  };

  const setConversationBlur = (shouldBlur: boolean) => {
    setIsConversationBlurred(shouldBlur);
  };

  const handleUpdateFavorite = (event: React.MouseEvent, isFavorite: boolean) => {
    event.stopPropagation();

    if (!isFavorite) {
      emitStarConversationEvent(user);
    }

    return markConversationAsFavoriteOrUnfavorite(conversationId, isFavorite);
  };

  return (
    <ConditionalWrapper
      shouldRenderWrapperElement={isMobileView}
      wrapper={(children) => (
        <SwipeableConversationItem conversationId={conversationId} setConversationBlur={setConversationBlur}>
          {children}
        </SwipeableConversationItem>
      )}
    >
      <StyledListboxOption
        {...listboxOptionProps}
        aria-selected={isCurrentConversation ? true : undefined}
        data-testid="conversation-item"
        data-eventid={CONVERSATION_ITEM_SELECT_EVENT_TYPE_ID}
        ref={isConversationActive && shouldScrollToConversationItem ? activeConversationRef : null}
        $isActive={isConversationActive}
        $isSelected={isCurrentConversation}
        $isBlurred={isConversationBlurred}
      >
        <Box aria-hidden={true} css={{ padding: 'default' }}>
          <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
            <Paragraph
              className={FULLSTORY_EXCLUDE}
              data-testid="participant-name"
              textStyle={hasUnreadMessage ? 'body-bold' : 'body'}
              css={{ ...truncatedTextStyles, textTransform: 'capitalize', paddingBottom: 'tightest' }}
            >
              {participantDisplay}
            </Paragraph>
            <Text
              textStyle="body-xs"
              css={{
                marginLeft: 'loose',
                marginRight: 'tighter',
                whiteSpace: 'nowrap',
              }}
            >
              {messageDateText}
            </Text>
          </Box>

          {shouldShowAddress && (
            <Paragraph data-testid="address" textStyle="body-xs">
              {addressText}
            </Paragraph>
          )}

          <Box data-testid="message-preview" css={{ display: 'flex', alignItems: 'center', paddingBlock: 'tighter' }}>
            {hasUnreadMessage && <StyledUnreadBadgeIndicator data-testid="unread-badge" aria-label="message unread" />}

            {messageText ? (
              <>
                <Paragraph
                  css={{
                    color: lastMessageFailed ? 'red.600' : '',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lastMessageFailed && <StyledIconError fontColor="red500" size="xs" />}

                  {isSpam ||
                    (isArchived && (
                      <StyledSpanItemIconContainer>
                        {isSpam && <IconSpam isGray={true} />}
                        {!isSpam && isArchived && <IconArchived isGray={true} />}
                      </StyledSpanItemIconContainer>
                    ))}

                  {messageText}
                </Paragraph>

                <IconButtonTooltip
                  content="Star conversation"
                  trigger={
                    <IconButton
                      data-eventid={STAR_CONVERSATION_EVENT_TYPE_ID}
                      shape="circle"
                      emphasis="bare"
                      tone={isFavorite ? 'brand' : 'neutral'}
                      disabled={isStarConversationIconDisabled}
                      icon={isFavorite ? <IconStar100Percent /> : <IconStar0Percent />}
                      onClick={(event: React.MouseEvent) => handleUpdateFavorite(event, isFavorite)}
                      title="star"
                      css={{
                        position: statusLabel ? 'absolute' : 'relative',
                        right: statusLabel ? 'default' : 0,
                        bottom: statusLabel ? '15px' : 0,
                        marginLeft: 'auto',
                        flexShrink: 0,
                      }}
                    />
                  }
                />
              </>
            ) : (
              <Paragraph>({StringConstants.UNABLE_LOAD_MESSAGE_TEXT})</Paragraph>
            )}
          </Box>

          {!!statusLabel && (
            <Box data-testid="status-label" css={{ paddingTop: 'tighter', paddingBottom: 'tighter' }}>
              <StatusLabel linkId={statusLabel.linkId} text={statusLabel.text} />
            </Box>
          )}
        </Box>

        {/* For screen readers */}
        <VisuallyHidden>{`${isPhoneLead ? 'Phone lead:' : 'Message with:'} ${participantDisplay};`}</VisuallyHidden>
        <VisuallyHidden>{`${isMessageOwner ? 'You sent on:' : 'Received on:'} ${messageDateText};`}</VisuallyHidden>
        {shouldShowAddress && <VisuallyHidden>{`For property located at: ${addressText};`}</VisuallyHidden>}
        {!!statusLabel && <VisuallyHidden>{`Renter status: ${statusLabel.text};`}</VisuallyHidden>}
        {hasUnreadMessage && <VisuallyHidden>This message is currently unread;</VisuallyHidden>}
        <VisuallyHidden>
          {`Click to navigate to this message ${hasUnreadMessage ? 'and mark as read' : ''}`}
        </VisuallyHidden>
      </StyledListboxOption>
    </ConditionalWrapper>
  );
};

export default memo(ConversationItem);
